(ns ui.view.enrich.processes
  "The top bar's background processes: an icon with the number of running AI
   enrichment jobs, and a side panel listing every job of the account that is not
   resolved yet. A finished job offers its review and a discard; a failed one a
   dismiss. The side panel opens by itself when a job finishes, with that job
   highlighted. Starting a job opens it on that job alone, once the robot of
   ui.view.enrich.fly has landed on the icon. Jobs stay listed across reloads
   until saved or discarded."
  (:require
   [comms.sente :as sente]
   [common.localization :as loc]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.drawer.view :as drawer]
   [ui.enrich.apply :as enrich-apply]
   [ui.enrich.event :as enrich-event]
   [ui.enrich.model :as enrich-model]
   [ui.enrich.sub :as enrich-sub]
   [ui.icons :as icons]
   [ui.util :as uu]
   [ui.view.enrich.fly :as fly]))

(defn- process-state
  "How a process shows: :running, :ready (a result to review), :finished (a
   result the survey editor applies, nothing to review here), :failed,
   :unfetched when its result could not be loaded, or :applying while its
   values are saved onto its target and, for a result that created a
   transaction process, :unapplied when some could not be."
  [{:keys [status target-id applying? fetch-failed?] :as process}]
  (cond
    fetch-failed?                               :unfetched
    (enrich-model/in-progress-status? status)   :running
    (enrich-model/failed-status? status)        :failed
    applying?                                   :applying
    (and target-id
         (= :tp-create (enrich-model/job-kind process))) :unapplied
    (= :survey (enrich-model/job-kind process)) :finished
    :else                                       :ready))

(defn- state-icon
  [state]
  (case state
    (:running :applying)
    [icons/loader-circle-icon {:size 16 :class "shrink-0 animate-spin text-primary-regular"}]

    (:failed :unapplied :unfetched)
    [icons/triangle-alert-icon {:size 16 :class "shrink-0 text-danger-light-text"}]

    [icons/circle-check-icon {:size 16 :class "shrink-0 text-success-regular"}]))

(defn- process-title
  "The name of the entity the job fills, which the backend adds to the job when
   there is one, else the job's label (a template name, or a generic one for
   other flows)."
  [process]
  (or (:entity-name process)
      (:label process)
      (loc/tr :enrich-process/untitled)))

(defn- process-subtitle
  [process state]
  (loc/tr (if (= state :failed)
            (enrich-model/failure-tr-key process :enrich-process/failed)
            (case state
              :running   :enrich-process/running
              :applying  :enrich-process/applying
              :ready     :enrich-process/ready
              :unapplied :enrich-process/unapplied
              :unfetched :enrich-process/unfetched
              :finished  :enrich-process/finished))))

(defn- process-row
  [{:keys [process highlighted?]}]
  (let [state     (process-state process)
        result-id (:result-id process)
        dismiss!  (if result-id
                    #(rf/dispatch [::enrich-event/resolve result-id])
                    #(rf/dispatch [::enrich-event/dismiss-request (:token process)]))]
    [:div {:class (uu/cm "flex flex-col gap-2 rounded-lg border border-solid border-base-light-border-paler p-3"
                         (when highlighted? "bg-primary-regular/5"))}
     [:div {:class "flex items-center gap-3"}
      [:div {:class "flex flex-1 min-w-0 flex-col"}
       [:span {:class "text-sm m-0 font-medium break-words"}
        (process-title process)]
       [:span {:class "text-xs m-0 text-base-light-text-paler break-words"}
        (process-subtitle process state)]]
      [state-icon state]]
     (case state
       :ready
       [:div {:class "flex gap-2"}
        [button/sm-primary-solid
         {:on-click #(rf/dispatch [::enrich-event/review result-id])}
         (loc/tr :enrich-process/review)]
        [button/sm-secondary-hollow
         {:on-click dismiss!}
         (loc/tr :enrich-process/discard)]]

       :unapplied
       [:div {:class "flex gap-2"}
        [button/sm-primary-solid
         {:on-click #(rf/dispatch [::enrich-apply/retry result-id])}
         (loc/tr :enrich-process/retry)]
        [button/sm-secondary-hollow
         {:on-click dismiss!}
         (loc/tr :enrich-process/dismiss)]]

       :unfetched
       [:div {:class "flex gap-2"}
        [button/sm-primary-solid
         {:on-click #(rf/dispatch [::enrich-event/fetch-result result-id {:finished? true}])}
         (loc/tr :enrich-process/retry)]
        [button/sm-secondary-hollow
         {:on-click dismiss!}
         (loc/tr :enrich-process/dismiss)]]

       (:failed :finished)
       [:div {:class "flex gap-2"}
        [button/sm-secondary-hollow
         {:on-click dismiss!}
         (loc/tr :enrich-process/dismiss)]]

       nil)]))

(defn- process-list
  []
  (let [processes @(rf/subscribe [::enrich-sub/shown-processes])
        {:keys [highlight focus]} @(rf/subscribe [::enrich-sub/menu])]
    (if (seq processes)
      [:div {:class "flex flex-col gap-3"}
       (doall
        (for [process processes]
          ^{:key (or (:result-id process) (:token process))}
          [process-row {:process      process
                        :highlighted? (or (and highlight
                                               (= highlight (:result-id process)))
                                          (and focus (= focus (:token process))))}]))]
      [:div {:class "text-sm m-0 py-3 text-base-light-text-paler"}
       (loc/tr :enrich-process/empty)])))

(defn- trigger
  []
  (let [running @(rf/subscribe [::enrich-sub/running-count])]
    [button/secondary-open
     {:class    "relative"
      :dom-id   fly/target-dom-id
      :tooltip  (loc/tr :enrich-process/title)
      :on-click #(rf/dispatch [::enrich-event/set-menu-open true])}
     [:i {:class (uu/cm "fas fa-bars-progress text-xl"
                        (when (pos? running) "text-primary-regular"))}]
     (when (pos? running)
       [:span {:class "absolute top-1 right-0 min-w-4 rounded-full px-1 text-xs m-0
                       leading-4 text-center bg-secondary-regular text-secondary-regular-text"}
        running])]))

(defn- reload-after-reconnect
  "A job that finished while the connection was down sent its completion to
   no one: list the jobs again when it is back."
  [{:keys [old-state new-state first-connect?]}]
  (when (and (:open? new-state)
             (not (:open? old-state))
             (not first-connect?))
    (rf/dispatch [::enrich-event/load-jobs])))

(defn indicator
  "The top bar icon and its dropdown, for users who may run AI enrichment.
   Mounting it loads the account's unresolved jobs, and loads them again after
   the connection comes back."
  []
  (r/create-class
   {:component-did-mount
    (fn [_]
      (sente/add-state-listener! ::reconnect reload-after-reconnect)
      (rf/dispatch [::enrich-event/load-jobs]))
    :component-will-unmount
    (fn [_]
      (sente/remove-state-listener! ::reconnect))
    :reagent-render
    (fn []
      (let [open? (:open? @(rf/subscribe [::enrich-sub/menu]))]
        [:<>
         [trigger]
         [drawer/view
          {:visible?      (boolean open?)
           :hide!         #(rf/dispatch [::enrich-event/set-menu-open false])
           :close-button? true
           :size          :md
           :title         (loc/tr :enrich-process/title)
           :body          [process-list]}]]))}))

(defn view
  []
  (when (session/has-feature-access? :feature/ai-enrichment)
    [indicator]))
